import { Box, Flex } from "@chakra-ui/react";
import { useMemo, useState, useCallback } from "react";
import { SortOption } from "components/ControlBar/types";
import DisplayActionsByStage from "features/Resources/Shared/Components/ActionsByStage";
import {
  filterActionSummaryByText,
  filterByActionType,
  filterByTargetRelationType,
} from "features/Resources/Shared/helpers";
import { ActionSummary, ActionType } from "features/Resources/Shared/models";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import ScheduleFilters from "./ScheduleFilters";

const DEFAULT_SORT = "date_asc";

const SORT_OPTIONS: SortOption[] = [
  { label: "Date (Earliest first)", value: "date_asc" },
  { label: "Date (Latest first)", value: "date_desc" },
  { label: "Type", value: "type" },
  { label: "Name", value: "name" },
];

function sortActions(actions: ActionSummary[], sort: string): ActionSummary[] {
  const sorted = [...actions];
  switch (sort) {
    case "date_desc":
      return sorted.sort((a, b) => new Date(b.unifiedDate || 0).getTime() - new Date(a.unifiedDate || 0).getTime());
    case "type":
      return sorted.sort((a, b) => a.actionType.localeCompare(b.actionType));
    case "name":
      return sorted.sort((a, b) => (a.action?.name || "").localeCompare(b.action?.name || ""));
    case "date_asc":
    default:
      return sorted.sort((a, b) => new Date(a.unifiedDate || 0).getTime() - new Date(b.unifiedDate || 0).getTime());
  }
}

interface ScheduleByStagesProps {
  actions: ActionSummary[];
  newHireStartDate: string | undefined;
  isPending: boolean;
  onActionClick: (action: ActionSummary) => void;
  showFilters?: boolean;
}

export default function ScheduleByStages({
  actions,
  newHireStartDate,
  isPending,
  onActionClick,
  showFilters = true,
}: ScheduleByStagesProps) {
  const [searchText, setSearchText] = useState("");
  const [hasIssues, setHasIssues] = useState(false);
  const [viewUnsent, setViewUnsent] = useState(false);
  const [viewCompleted, setViewCompleted] = useState(false);
  const [viewUpcoming, setViewUpcoming] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState<{ label: string; value: string } | null>(null);
  const [selectedActionType, setSelectedActionType] = useState<{ label: string; value: string } | null>(null);
  const [currentSort, setCurrentSort] = useState(DEFAULT_SORT);

  // Derive filter values from selected options
  const relationTargetType = (selectedStakeholder?.value as RelationType) || "";
  const actionType = (selectedActionType?.value as ActionType) || "";

  const filteredActions = useMemo(() => {
    let filtActions = filterActionSummaryByText(actions, searchText);
    filtActions = filterByActionType(filtActions, actionType);
    filtActions = filterByTargetRelationType(filtActions, relationTargetType);

    // Filter by has issues
    if (hasIssues) {
      filtActions = filtActions.filter((action) => action.action?.issueIds && action.action.issueIds.length > 0);
    }

    // Filter by unsent (failed status)
    if (viewUnsent) {
      filtActions = filtActions.filter((action) => {
        const status = action.action?.status;
        return status === "failed";
      });
    }

    // Filter by view past (completed/delivered/skipped status)
    if (viewCompleted) {
      filtActions = filtActions.filter((action) => {
        const status = action.action?.status;

        // Tasks: completed or skipped status
        if (action.actionType === "task") {
          return status === "completed" || status === "skipped" || status === "closed_stale";
        }

        // Messages/Emails: delivered or skipped status
        if (action.actionType === "message" || action.actionType === "email") {
          return status === "delivered" || status === "skipped";
        }

        return false;
      });
    }

    // Filter by view upcoming (pending/upcoming status)
    if (viewUpcoming) {
      filtActions = filtActions.filter((action) => {
        const status = action.action?.status;

        // Tasks: upcoming status
        if (action.actionType === "task") {
          return status === "upcoming";
        }

        // Messages/Emails: pending status
        if (action.actionType === "message" || action.actionType === "email") {
          return status === "pending";
        }

        return false;
      });
    }

    return sortActions(filtActions, currentSort);
  }, [
    actionType,
    actions,
    relationTargetType,
    searchText,
    hasIssues,
    viewUnsent,
    viewCompleted,
    viewUpcoming,
    currentSort,
  ]);

  const handleReset = useCallback(() => {
    setSearchText("");
    setHasIssues(false);
    setViewUnsent(false);
    setViewCompleted(false);
    setViewUpcoming(false);
    setSelectedStakeholder(null);
    setSelectedActionType(null);
    setCurrentSort(DEFAULT_SORT);
  }, []);

  // Create a wrapper that calls the parent's onActionClick
  const expandClickHandlerByAction = useCallback(
    (action: ActionSummary) => {
      onActionClick(action);
    },
    [onActionClick],
  );

  return (
    <Flex direction="column">
      {showFilters && (
        <ScheduleFilters
          searchText={searchText}
          onSearchChange={setSearchText}
          hasIssues={hasIssues}
          onHasIssuesChange={setHasIssues}
          viewUnsent={viewUnsent}
          onViewUnsentChange={setViewUnsent}
          viewCompleted={viewCompleted}
          onViewCompletedChange={setViewCompleted}
          viewUpcoming={viewUpcoming}
          onViewUpcomingChange={setViewUpcoming}
          selectedStakeholder={selectedStakeholder}
          onStakeholderChange={setSelectedStakeholder}
          selectedActionType={selectedActionType}
          onActionTypeChange={setSelectedActionType}
          onReset={handleReset}
          currentSort={currentSort}
          defaultSort={DEFAULT_SORT}
          onSortChange={setCurrentSort}
          sortOptions={SORT_OPTIONS}
        />
      )}

      <Box overflowX="auto" width="100%" mt="4">
        {newHireStartDate && (
          <DisplayActionsByStage
            startDate={newHireStartDate}
            actions={filteredActions}
            expandClickHandlerByAction={expandClickHandlerByAction}
            isPending={isPending}
          />
        )}
      </Box>
    </Flex>
  );
}
